import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  CatalogueItem,
  Department,
  WorkOrderService,
} from '../../api/endpoints.js';
import { ApiError } from '../../api/client.js';
import {
  ActionButton,
  Alert,
  ConfirmDialog,
  newRowKey,
  NumberField,
  SelectField,
  TextAreaField,
  patchRow,
  withoutRow,
} from '../../components/ui/index.js';
import { IconRemove } from '../../components/ui/nav-icons.js';
import { formatMoney } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { toDraft, type BilingualDraft } from '../clients/bilingual.js';

/**
 * The list of services a request asks for.
 *
 * ===========================================================================
 * READ CLAUDE.md SECTION 4 BEFORE ADDING A FIELD TO THIS EDITOR.
 * ===========================================================================
 *
 * A work order REQUESTS services. Every input here is COMMERCIAL: which
 * catalogue entry, how many, at what price, out of which department, with a
 * note. There is deliberately no reading, no unit of measurement, no formula,
 * no rounding rule, no acceptance limit and no chart — for any test, including
 * the five initial ones. The readiness of a specialised template is SHOWN, and
 * what it says today is that there is none.
 *
 * Shared by the create form, the edit form and the scope-change dialog on
 * purpose: the same list of services, collected the same way and sent the same
 * way (`servicePayload`), so the screens cannot drift into asking for — or
 * saving — different things.
 *
 * A SAVED SERVICE KEEPS ITS IDENTITY (D195). A row loaded from the work order
 * carries its `id`, so saving changes that service IN PLACE — its line number,
 * its agreed price and anything already linked to it stay — instead of
 * deleting it and adding a look-alike. A row whose work has STARTED is shown
 * but cannot be changed here; the server refuses it too.
 */

export interface ServiceLineDraft {
  /** The row's own key — never its position (see components/ui/rows.ts). */
  readonly key: string;
  /** The saved service this row edits; null for a row added here. */
  readonly id: string | null;
  /** The saved service's state; `requested` for a new row. */
  readonly state: string;
  readonly lineNumber: number | null;
  catalogueItemId: string | null;
  /** For a line with nothing in the catalogue behind it — a call-out, say. */
  description: BilingualDraft;
  departmentId: string | null;
  quantity: number | null;
  /** Empty means "let the server resolve it". Never 0. */
  unitPrice: number | null;
  /** The saved price the row was agreed at (agreement, else catalogue). */
  readonly referencePriceMinor: number | null;
  priceOverrideReason: string;
  discountPercent: number | null;
  /** A fixed-amount discount carried from the saved line; kept unless replaced. */
  discountAmountMinor: number | null;
  notes: string;
}

export const emptyServiceLine = (): ServiceLineDraft => ({
  key: newRowKey('service-line'),
  id: null,
  state: 'requested',
  lineNumber: null,
  catalogueItemId: null,
  description: toDraft(null),
  departmentId: null,
  quantity: 1,
  unitPrice: null,
  referencePriceMinor: null,
  priceOverrideReason: '',
  discountPercent: null,
  discountAmountMinor: null,
  notes: '',
});

/** A saved service as an editable row — its price and discount as they stand. */
export function draftFromService(service: WorkOrderService): ServiceLineDraft {
  return {
    key: newRowKey('service-line'),
    id: service.id,
    state: service.state,
    lineNumber: service.lineNumber,
    catalogueItemId: service.catalogueItemId,
    description: toDraft(service.description),
    departmentId: service.department?.id ?? null,
    quantity: service.quantity,
    unitPrice: service.unitPriceMinor / 100,
    referencePriceMinor: service.agreementPriceMinor ?? service.cataloguePriceMinor,
    priceOverrideReason: service.priceOverrideReason ?? '',
    discountPercent: service.discountPercent,
    discountAmountMinor: service.discountAmountMinor,
    notes: service.notes ?? '',
  };
}

/** A saved service whose work has begun or ended: shown, never edited here. */
export const isLockedLine = (line: ServiceLineDraft) =>
  line.id !== null && line.state !== 'requested';

/**
 * The request body for a list of rows. A saved row is ALWAYS sent, with its
 * `id`, so the server changes it in place and never mistakes an emptied field
 * for a removal; a new row is sent only once it names a service and a quantity.
 */
export function servicePayload(lines: readonly ServiceLineDraft[]) {
  return lines
    .filter(
      (line) =>
        line.id !== null ||
        ((line.catalogueItemId || line.description.en || line.description.ar) &&
          line.quantity),
    )
    .map((line) => ({
      ...(line.id ? { id: line.id } : {}),
      ...(line.catalogueItemId
        ? { catalogueItemId: line.catalogueItemId }
        : {
            description: {
              ar: line.description.ar || null,
              en: line.description.en || null,
            },
          }),
      ...(line.id
        ? { departmentId: line.departmentId }
        : line.departmentId
          ? { departmentId: line.departmentId }
          : {}),
      quantity: line.quantity as number,
      // Absent unless a price is in the box. An empty price is not a price of
      // zero (CLAUDE.md section 8); on a saved row the box holds its price.
      ...(line.unitPrice === null
        ? {}
        : { unitPriceMinor: Math.round(line.unitPrice * 100) }),
      ...(line.priceOverrideReason.trim()
        ? { priceOverrideReason: line.priceOverrideReason.trim() }
        : {}),
      ...(line.id
        ? {
            discountPercent: line.discountPercent,
            discountAmountMinor: line.discountAmountMinor,
          }
        : line.discountPercent === null
          ? {}
          : { discountPercent: line.discountPercent }),
      ...(line.id
        ? { notes: line.notes.trim() || null }
        : line.notes.trim()
          ? { notes: line.notes.trim() }
          : {}),
    }));
}

/**
 * Why the server refused a change to the services (D195), in the reader's
 * language. The API's generic CONFLICT sentence says nothing about WHICH rule
 * applied; the detail code does, so it is explained here instead.
 */
const SERVICE_REFUSALS = [
  'service_in_use',
  'service_invoiced',
  'service_started',
  'service_cancelled',
  'service_not_on_work_order',
  'service_identity_changed',
  'impact_not_acknowledged',
] as const;

export function ServiceRefusal({ error }: { readonly error: unknown }) {
  const { t } = useTranslation();
  if (!(error instanceof ApiError)) return null;
  const code = SERVICE_REFUSALS.find((candidate) =>
    error.details?.some((detail) => detail.code === candidate),
  );
  if (!code) return null;
  return (
    <div className="mb-stack">
      <Alert tone="warning" title={t(`workOrders.serviceRefusal.${code}`)} />
    </div>
  );
}

export interface ServiceLineEditorProps {
  readonly lines: readonly ServiceLineDraft[];
  readonly onChange: (lines: ServiceLineDraft[]) => void;
  readonly catalogue: readonly CatalogueItem[];
  readonly departments: readonly Department[];
  readonly canOverride: boolean;
  readonly currency?: string;
  readonly isDisabled?: boolean;
}

export function ServiceLineEditor({
  lines,
  onChange,
  catalogue,
  departments,
  canOverride,
  currency,
  isDisabled = false,
}: ServiceLineEditorProps) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const [confirmRemoval, setConfirmRemoval] = useState<ServiceLineDraft | null>(null);

  const patch = (key: string, change: Partial<Omit<ServiceLineDraft, 'key'>>) => {
    onChange(patchRow(lines, key, change));
  };

  const bilingual = (value: { ar: string | null; en: string | null }) =>
    (locale === 'ar' ? value.ar : value.en) ?? value.en ?? value.ar ?? '';

  return (
    <div className="grid min-w-0 gap-gutter">
      {lines.length === 0 ? (
        <p className="text-body text-default-600">{t('common.noLinesYet')}</p>
      ) : null}
      {lines.map((line, index) => {
        const item = catalogue.find(
          (candidate) => candidate.id === line.catalogueItemId,
        );
        const locked = isDisabled || isLockedLine(line);
        const referenceMinor = line.id ? line.referencePriceMinor : (item?.basePriceMinor ?? null);
        const typedMinor = line.unitPrice === null ? null : Math.round(line.unitPrice * 100);

        return (
          // One block per service: what it is (its saved number and state, or
          // its place in the list) and its removal in a header strip, the
          // fields beneath (D278).
          <div
            key={line.key}
            className="min-w-0 rounded-medium border border-default-200"
            data-testid="service-line"
          >
            <div className="flex min-h-10 flex-wrap items-center gap-inline border-b border-default-200 bg-default-50 px-3 py-1.5">
              <p className="text-caption font-semibold text-default-800">
                {line.id ? (
                  <>
                    {t('workOrders.serviceLineNumber', { number: line.lineNumber })}
                    {' · '}
                    {t(`workOrderServiceState.${line.state}`)}
                  </>
                ) : (
                  t('ordersRecord.lineNumber', { n: index + 1 })
                )}
              </p>
              {!locked ? (
                <span className="ms-auto">
                  <ActionButton
                    priority="destructive"
                    size="sm"
                    startContent={<IconRemove size={16} />}
                    onPress={() =>
                      // A row never saved goes at once; a saved service is
                      // confirmed, because saving then takes it off the order.
                      line.id
                        ? setConfirmRemoval(line)
                        : onChange(withoutRow(lines, line.key))
                    }
                  >
                    {t('workOrders.removeService')}
                  </ActionButton>
                </span>
              ) : null}
            </div>
            <div className="grid min-w-0 grid-cols-1 gap-stack p-3 sm:grid-cols-2">

              <SelectField
                label={t('workOrders.service')}
                placeholder={t('workOrders.selectService')}
                offerNoChoice={false}
                value={line.catalogueItemId}
                onChange={(value) =>
                  patch(line.key, {
                    catalogueItemId: value,
                    // The department the catalogue entry names is offered as a
                    // starting point, not imposed: a laboratory may run one test
                    // out of a different department on a given job.
                    departmentId:
                      catalogue.find((candidate) => candidate.id === value)
                        ?.department?.id ?? line.departmentId,
                  })
                }
                // A saved service stays the test it is. Another test is a
                // removal and an addition, which is what the server records.
                isDisabled={locked || line.id !== null}
                options={catalogue.map((entry) => ({
                  value: entry.id,
                  label: bilingual(entry.name) || entry.code || '',
                  // An unpriced entry says so here, so the desk knows a price
                  // will have to be typed before this saves. Null is UNPRICED;
                  // it is never a price of zero.
                  description:
                    entry.basePriceMinor === null
                      ? t('catalogue.notPriced')
                      : (formatMoney(entry.basePriceMinor, {
                          locale,
                          currency: entry.currency,
                        }) ?? undefined),
                }))}
              />

              <NumberField
                label={t('workOrders.quantity')}
                value={line.quantity}
                onChange={(value) => patch(line.key, { quantity: value })}
                step="0.001"
                isReadOnly={locked}
              />

              {/*
                The laboratory's own department register. A reference, never
                free text: two people typing the same department slightly
                differently produced two departments, which is what migration
                0005 had to repair.
              */}
              <SelectField
                label={t('workOrders.department')}
                placeholder={t('common.none')}
                value={line.departmentId}
                onChange={(value) => patch(line.key, { departmentId: value })}
                isDisabled={locked}
                options={departments.map((department) => ({
                  value: department.id,
                  label: department.name || department.code || '',
                }))}
              />

              {canOverride ? (
                <NumberField
                  label={t('workOrders.unitPrice')}
                  description={t('workOrders.unitPriceHint')}
                  value={line.unitPrice}
                  onChange={(value) => patch(line.key, { unitPrice: value })}
                  step="0.01"
                  {...(currency ? { unit: currency } : {})}
                  isReadOnly={locked}
                />
              ) : null}

              {/*
                The reason appears only once a price is typed that DIFFERS from
                the one the line was agreed at (a saved line) or the catalogue's
                (a new one). Asking for one unconditionally trains people to
                write one without meaning it.
              */}
              {canOverride && typedMinor !== null && referenceMinor !== null && typedMinor !== referenceMinor ? (
                <TextAreaField
                  label={t('workOrders.priceOverrideReason')}
                  value={line.priceOverrideReason}
                  onChange={(value) =>
                    patch(line.key, { priceOverrideReason: value })
                  }
                  rows={2}
                  isRequired
                  isReadOnly={locked}
                />
              ) : null}

              <NumberField
                label={t('workOrders.lineDiscount')}
                {...(line.discountAmountMinor !== null && line.discountPercent === null
                  ? {
                      description: t('workOrders.amountDiscountKept', {
                        amount: formatMoney(line.discountAmountMinor, { locale, currency: currency ?? '' }) ?? '',
                      }),
                    }
                  : {})}
                value={line.discountPercent}
                // A percentage replaces a fixed amount: one discount per line.
                onChange={(value) =>
                  patch(line.key, {
                    discountPercent: value,
                    ...(value !== null ? { discountAmountMinor: null } : {}),
                  })
                }
                step="0.01"
                isReadOnly={locked}
              />

              <TextAreaField
                label={t('workOrders.serviceNotes')}
                value={line.notes}
                onChange={(value) => patch(line.key, { notes: value })}
                rows={2}
                isReadOnly={locked}
              />

              {/* Said only when specialised execution is not available (CLAUDE.md section 4). */}
              {item && item.templateReadiness !== 'ready' ? (
                <p className="text-caption text-default-600 sm:col-span-2">
                  {t('workOrders.generalWorkflowOnly')}
                </p>
              ) : null}

              {isLockedLine(line) ? (
                <p className="text-caption text-default-700 sm:col-span-2">
                  {t('workOrders.startedServiceLocked')}
                </p>
              ) : null}

            </div>
          </div>
        );
      })}

      <ConfirmDialog
        isOpen={confirmRemoval !== null}
        onClose={() => setConfirmRemoval(null)}
        title={t('workOrders.removeServiceConfirmTitle')}
        message={t('workOrders.removeServiceConfirmBody', {
          number: confirmRemoval?.lineNumber ?? '',
        })}
        confirmLabel={t('workOrders.removeService')}
        onConfirm={() => {
          if (confirmRemoval) onChange(withoutRow(lines, confirmRemoval.key));
          setConfirmRemoval(null);
        }}
      />
    </div>
  );
}
